import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBluetooth } from '../context/BluetoothContext';
import './DeviceConfig.css';

const DeviceConfig: FC = () => {
    const navigate = useNavigate();
    const { deviceId } = useParams<{ deviceId: string }>();
    const { devices, send, updateDeviceMapping, toggleDevice } = useBluetooth();

    const device = devices.find(d => d.id === deviceId);
    const [isLocked, setIsLocked] = useState(false);
    const [randomMode, setRandomMode] = useState(false);

    if (!device) {
        return (
            <div className="device-config-container">
                <h1>Device Not Found</h1>
                <button onClick={() => navigate('/')} className="back-button">
                    Back to Home
                </button>
            </div>
        );
    }

    const handleMappingChange = (value: number) => {
        updateDeviceMapping(device.id, value);
    };

    const handleToggleLock = async () => {
        const newState = !isLocked;
        setIsLocked(newState);
        await send({ msg: `toggle-buttons "${newState ? 'off' : 'on'}"`, deviceId: device.id });
    };

    const handleToggleDevice = async () => {
        toggleDevice(device.id);
    };

    const handleRandomMode = async () => {
        const newState = !randomMode;
        setRandomMode(newState);

        if (newState) {
            // Start random mode - turn on with random effect
            return send({ msg: `set-sequence "100,50,50,80,30" "2000,3000,4000,2000,5000"`, deviceId: device.id });
        } else {
            // Stop random mode - turn off
            await send({ msg: 'turn-off', deviceId: device.id });
        }
    };

    return (
        <div className="device-config-container">
            <header className="config-header">
                <button onClick={() => navigate('/')} className="back-button">
                    ← Back
                </button>
                <h1>{device.name}</h1>
            </header>

            <div className="config-content">
                <div className="config-section">
                    <h2>Device Settings</h2>

                    <div className="config-item">
                        <label className="toggle-label">
                            <span>Enable Device</span>
                            <div className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={device.enabled}
                                    onChange={handleToggleDevice}
                                />
                                <span className="toggle-slider"></span>
                            </div>
                        </label>
                    </div>

                    <div className="config-item">
                        <label className="toggle-label">
                            <span>Lock Physical Buttons</span>
                            <div className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={isLocked}
                                    onChange={handleToggleLock}
                                />
                                <span className="toggle-slider"></span>
                            </div>
                        </label>
                        <p className="config-description">
                            When enabled, physical buttons on the device will be disabled
                        </p>
                    </div>

                    <div className="config-item">
                        <label className="toggle-label">
                            <span>Random Mode</span>
                            <div className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={randomMode}
                                    onChange={handleRandomMode}
                                />
                                <span className="toggle-slider"></span>
                            </div>
                        </label>
                        <p className="config-description">
                            Enable random vibration patterns and intensities
                        </p>
                    </div>
                </div>

                <div className="config-section">
                    <h2>Sensitivity Mapping</h2>
                    <div className="mapping-control">
                        <div className="mapping-header">
                            <span>Mapping Value</span>
                            <span className="mapping-value">{device.mapping.toFixed(2)}</span>
                        </div>
                        <input
                            type="range"
                            min="-1"
                            max="1"
                            step="0.1"
                            value={device.mapping}
                            onChange={(e) => handleMappingChange(parseFloat(e.target.value))}
                            className="mapping-slider"
                        />
                        <div className="mapping-labels">
                            <span>-1.0 (Inverse)</span>
                            <span>0.0 (Neutral)</span>
                            <span>1.0 (Direct)</span>
                        </div>
                        <p className="config-description">
                            Adjust how touch input translates to device intensity
                        </p>
                    </div>
                </div>

                <div className="config-section">
                    <h2>Device Information</h2>
                    <div className="device-info">
                        <div className="info-item">
                            <span className="info-label">Device ID:</span>
                            <span className="info-value">{device.id}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Status:</span>
                            <span className={`info-value status ${device.enabled ? 'enabled' : 'disabled'}`}>
                                {device.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                        {device.RSSI && (
                            <div className="info-item">
                                <span className="info-label">Signal Strength:</span>
                                <span className="info-value">{device.RSSI} dB</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="config-actions">
                    <button
                        onClick={() => navigate('/touch')}
                        className="primary-button"
                        disabled={!device.enabled}
                    >
                        Open Controller
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeviceConfig;
